import { useCallback, useEffect, useRef } from 'react';

export function useUndoRedo<T>(
  state: T,
  setState: (val: T) => void,
  debounceMs: number = 350,
  maxHistory: number = 50
) {
  const undoStack = useRef<T[]>([]);
  const redoStack = useRef<T[]>([]);
  const isHistoryAction = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedJson = useRef<string>('');

  const undo = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current.pop()!;
    redoStack.current.push(JSON.parse(JSON.stringify(state)));
    isHistoryAction.current = true;
    lastSavedJson.current = JSON.stringify(prev);
    setState(prev);
  }, [state, setState]);

  const redo = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (redoStack.current.length === 0) return;
    const next = redoStack.current.pop()!;
    undoStack.current.push(JSON.parse(JSON.stringify(state)));
    isHistoryAction.current = true;
    lastSavedJson.current = JSON.stringify(next);
    setState(next);
  }, [state, setState]);

  useEffect(() => {
    if (isHistoryAction.current) {
      isHistoryAction.current = false;
      return;
    }
    const currentJson = JSON.stringify(state);
    if (!lastSavedJson.current) {
      lastSavedJson.current = currentJson;
      return;
    }
    if (currentJson === lastSavedJson.current) return;

    const snapshot = JSON.parse(lastSavedJson.current);
    if (timer.current) {
      clearTimeout(timer.current);
    } else {
      undoStack.current.push(snapshot);
      if (undoStack.current.length > maxHistory) undoStack.current.shift();
      redoStack.current = [];
    }

    timer.current = setTimeout(() => {
      timer.current = null;
      lastSavedJson.current = currentJson;
    }, debounceMs);
  }, [state, debounceMs, maxHistory]);

  return { undo, redo };
}
